import {useEffect} from 'react'
import {useHistory} from 'react-router-dom'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {PATHS} from 'src/constants/constants'
import {cleanUpHandledNotifications} from './notificationCleanup'

// Messages broadcast by public/service-worker.js to the open portal pages
export const CHANNEL_NAME = 'web-push'
export const REMINDER_PUSH_MESSAGE = 'assumption-reminder'
export const ACTIVITY_PUSH_MESSAGE = 'activity-change'
export const OPEN_REMINDER_MESSAGE = 'open-reminder'
// Left by the service worker in Cache Storage when a click had to open the app (see there)
export const PENDING_CACHE = 'web-push'
export const PENDING_KEY = '/pending-reminder-click'
// A pending click older than this is stale (e.g. the app was opened much later by other means).
// It also has to outlast a login: a click without a session goes through the login page, and the
// pending click is read once the user is back in the portal (this hook is mounted only then).
const PENDING_MAX_AGE_MS = 15 * 60 * 1000

const GENERIC_MESSAGE = 'Hai un promemoria per la tua terapia.'
// How long a reminder or an activity change stays on screen inside the page
const TOAST_DURATION_MS = 30000

// Only paths of this portal: a message must never send the user to another site.
function reminderPath(url?: string): string {
  return url && url.startsWith('/') && !url.startsWith('//') ? url : PATHS.REMINDER
}

// Where a click on the notification wanted to go, when the app was started by that click: an
// installed app may start from its start_url instead of the URL the service worker asked for, and
// a click without a session lands on the login page, which then goes to '/'.
async function consumePendingClick(): Promise<string | undefined> {
  if (!('caches' in window)) return undefined
  try {
    const cache = await caches.open(PENDING_CACHE)
    const pending = await cache.match(PENDING_KEY)
    if (!pending) return undefined
    const {url, clickedAt} = JSON.parse(await pending.text())
    await cache.delete(PENDING_KEY)
    return Date.now() - Number(clickedAt) < PENDING_MAX_AGE_MS ? reminderPath(url) : undefined
  } catch (error) {
    return undefined
  }
}

// Web push notifications inside the open portal: shows the reminder or the activity change in the
// page too (the system notification alone is easy to miss), and opens the dose to confirm or the
// activity when the notification is clicked - the page navigates itself, since the service worker
// cannot reliably do it on mobile.
// The click is delivered twice on purpose (broadcast message and pending click): whichever comes
// first navigates, the other finds the click already consumed or the page already there.
export function useReminderPush(): void {
  const {info} = useToast()
  const history = useHistory()

  useEffect(() => {
    const openReminder = (url?: string) => {
      const path = reminderPath(url)
      // the agenda link carries the activity in its query string
      if (`${history.location.pathname}${history.location.search}` !== path) history.push(path)
    }
    const openPendingClick = () => {
      consumePendingClick().then(path => { if (path) openReminder(path) })
    }
    // At startup and back in the foreground: the pending click, and the notifications already
    // handled on another device closed (RF13)
    const onForeground = () => {
      openPendingClick()
      cleanUpHandledNotifications()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onForeground()
    }

    onForeground()
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onForeground)

    const channel = 'BroadcastChannel' in window ? new BroadcastChannel(CHANNEL_NAME) : undefined
    if (channel) {
      channel.onmessage = (event: MessageEvent) => {
        if (event.data?.type === OPEN_REMINDER_MESSAGE) {
          consumePendingClick()
          openReminder(event.data.url)
        } else if (event.data?.type === REMINDER_PUSH_MESSAGE) {
          const {url, body} = event.data
          // the generic text of the notification points to the portal: here the user already is in it
          const message = url && url !== PATHS.REMINDER && body ? body : GENERIC_MESSAGE
          info(message, {
            title: 'Promemoria terapia',
            duration: TOAST_DURATION_MS,
            action: {label: 'Conferma l\'assunzione', onClick: () => openReminder(url)},
          })
        } else if (event.data?.type === ACTIVITY_PUSH_MESSAGE) {
          const {url, title, body} = event.data
          info(body, {
            title,
            duration: TOAST_DURATION_MS,
            action: {label: 'Vedi dettaglio', onClick: () => openReminder(url)},
          })
        }
      }
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onForeground)
      channel?.close()
    }
  }, [info, history])
}
